import { ref, watch, computed, onMounted } from "@vue/composition-api";
import store from "@/store";
import { formatCurrency } from "@core/utils/filter";

// Notification
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import ability from "@/libs/acl/ability";
import { getDateTime } from "@/libs/helper";

export default function useExpendituresList() {
  // Use toast
  const toast = useToast();

  const refExpenditureListTable = ref(null);

  // Table Handlers
  const tableColumns = [
    { key: "expense_number", label: "編號", sortable: true, class: 'sticky-column' },
    { key: "actions", label: "操作", class: 'sticky-column' },
    { key: "user.name_cn", label: "收支者" },
    { key: "salary_type", label: "薪資形式", sortable: true },
    { key: "year_month", label: "出糧月份", sortable: true },
    { key: "expense_date", label: "出糧日期", sortable: true },
    { key: "show_details", label: "工資詳情" },
    { key: "purpose_of_expenditure", label: "出糧備註" },
    { key: "salary_receive_method_id", label: "收款方式", sortable: true },
    { key: "check_number", label: "支票號碼", sortable: true },
    { key: "actual_salary", label: "實發工資", sortable: true, formatter: formatCurrency },
    { key: "total_amount", label: "公司總支出金額", sortable: true, formatter: formatCurrency },
    // { key: "invoice", label: "發票編號" },
    // { key: "invoice.address", label: "施工地址" },
    /* { key: "approve_status", label: "審批狀態", sortable: true }, */
    { key: "expense_status", label: "薪金支出狀態" },
    // { key: "is_approved", label: "已批核", sortable: true },
    // { key: "approve_remark", label: "批核備註"},
    { key: "created_at", label: "創建日期", sortable: true },
  ];
  const perPage = ref(10);
  const totalRow = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref("");
  const sortBy = ref("expense_date");
  const isSortDirDesc = ref(true);

  const dataMeta = computed(() => {
    const localItemsCount = refExpenditureListTable.value
      ? refExpenditureListTable.value.localItems.length
      : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalRow.value,
    };
  });

  const refetchData = () => {
    refExpenditureListTable.value.refresh();
  };

  const expendituresList = ref(null);

  const filter_invoice_id = ref(null);
  const filter_user_id = ref(null);
  const filter_salary_type = ref(null);
  const filter_expense_status_id = ref(null);
  const filter_start_date = ref(null);
  const filter_end_date = ref(null);
  const filter_min_amount = ref(null);
  const filter_max_amount = ref(null);


  watch(
    [currentPage, perPage, searchQuery,
      filter_invoice_id,
      filter_user_id,
      filter_salary_type,
      filter_expense_status_id,
      filter_start_date,
      filter_end_date,
      filter_min_amount,
      filter_max_amount
    ],
    () => {
      refetchData();
    }
  );

  const fetchExpenditures = (ctx, callback) => {


    store
      .dispatch("expenditure/fetchExpenditures", {
        q: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        invoice_id: filter_invoice_id.value,
        user_id: filter_user_id.value,
        salary_type: filter_salary_type.value,
        expense_status_id: filter_expense_status_id.value,
        start_date: filter_start_date.value,
        end_date: filter_end_date.value,
        min_amount: filter_min_amount.value,
        max_amount: filter_max_amount.value,
      })
      .then((response) => {
        const { expenses, total } = response.data;
        callback(expenses);
        totalRow.value = total;
        expendituresList.value = expenses
      })
      .catch((err) => {
        console.log(err);
        toast({
          component: ToastificationContent,
          props: {
            title: "獲取貸款清單時出錯",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
  };

  const invoiceOptions = ref([]);
  const workerOptions = ref([]);
  const userOptions = ref([]);
  const productOptions = ref([]);
  const expenseStatusOptions = ref([])
  const salaryReceiveMethodOptions = ref([])

  const refetchOption = () => {
    store
      .dispatch("app/fetchOptionList", {
        invoices: true,
        workers: true,
        users: true,
        products: true,
        expense_statuses: true,
        receive_methods: true,
      })
      .then((response) => {
        invoiceOptions.value = response.data.invoices;
        workerOptions.value = response.data.workers;
        userOptions.value = response.data.users;
        productOptions.value = response.data.products;
        expenseStatusOptions.value = response.data.expense_statuses;
        salaryReceiveMethodOptions.value = response.data.receive_methods.sort ((a, b) => a.sort_order - b.sort_order);

      });
  };

  onMounted(() => {
    refetchOption();
  });

  // 匯出記錄的主要函數
  const exportRecords = (exportType) => {
    return new Promise((resolve, reject) => {
      let dataToExport;

      if (exportType === 'current') {
        // 匯出當前頁面數據
        dataToExport = expendituresList.value;
        processExportData(dataToExport, resolve);
      } else {
        // 匯出全部數據 - 需要重新獲取所有記錄
        store
          .dispatch("expenditure/fetchExpenditures", {
            q: searchQuery.value,
            perPage: 999999, // 獲取所有記錄
            page: 1,
            sortBy: sortBy.value,
            sortDesc: isSortDirDesc.value,
            invoice_id: filter_invoice_id.value,
            user_id: filter_user_id.value,
            salary_type: filter_salary_type.value,
            expense_status_id: filter_expense_status_id.value,
            start_date: filter_start_date.value,
            end_date: filter_end_date.value,
            min_amount: filter_min_amount.value,
            max_amount: filter_max_amount.value,
          })
          .then((response) => {
            const { expenses } = response.data;
            processExportData(expenses, resolve);
          })
          .catch((error) => {
            console.error('獲取全部記錄失敗:', error);
            reject(error);
          });
      }
    });
  };

  // 處理匯出數據的共用函數
  const processExportData = (dataToExport, resolve) => {
    // 按出糧日期排序數據
    const sortedData = [...dataToExport].sort((a, b) => {
      const dateA = new Date(a.expense_date);
      const dateB = new Date(b.expense_date);
      return dateB - dateA; // 降序排列（最新的在前）
    });

    // 準備匯出數據
    const exportData = [];

    sortedData.forEach((item) => {
      // 處理工資詳情的複雜格式化
      const salaryDetails = formatSalaryDetails(item);

      exportData.push({
        '編號': item.expense_number || '',
        '收支者': item.user?.name_cn || '',
        '薪資形式': getSalaryTypeName(item.salary_type),
        '出糧月份': formatYearMonth(item.salary_year, item.salary_month),
        '出糧日期': item.expense_date || '',
        '工資詳情': salaryDetails,
        '出糧備註': item.purpose_of_expenditure || '',
        '收款方式': getSalaryReceiveMethodName(item.salary_receive_method_id),
        '支票號碼': item.check_number || '',
        '實發工資': formatCurrency(calculateActualSalary(item)),
        '公司總支出金額': formatCurrency(item.total_amount),
        '薪金支出狀態': item.expense_status?.name || '',
        '創建日期': getDateTime(item.created_at, true) || ''
      });
    });

    resolve(exportData);
  };

  // 格式化工資詳情的複雜函數
  const formatSalaryDetails = (item) => {
    if (!item.salary_records || item.salary_records.length === 0) {
      return '';
    }

    const details = [];

    item.salary_records.forEach((record, index) => {
      const user = userOptions.value?.find(u => u.id === record.user_id);
      if (!user) return;

      // 如果有多個記錄，添加記錄分隔標題
      if (item.salary_records.length > 1) {
        details.push(`=== 工資記錄 ${index + 1} ===`);
      }

      // 師傅資訊
      const workerInfo = `員工：[${user.position || ''}] ${user.name_cn}`;
      details.push(workerInfo);

      // 工資顯示（根據 salary_type）
      const salaryType = item.salary_type;
      if (salaryType === 1) {
        // 日薪
        details.push(`工資：${record.working_days || 0}天 × $${Number(record.day_salary || 0).toFixed(2)} = $${Number(record.total_salary || 0).toFixed(2)}`);
      } else if (salaryType === 2) {
        // 月薪
        details.push(`月薪：$${Number(record.total_salary || 0).toFixed(2)}`);
      } else if (salaryType === 3) {
        // 以工程計算
        details.push(`工程工資：$${Number(record.total_salary || 0).toFixed(2)}`);
      }

      // 逾時工作
      if (record.ot_amount && Number(record.ot_amount) > 0) {
        details.push(`逾時工作：${record.ot_hours || 0}小時 = $${Number(record.ot_amount).toFixed(2)}`);
      } else {
        details.push('逾時工作：--');
      }

      // 佣金/花紅
      details.push(`佣金/花紅：$${Number(record.commission || 0).toFixed(2)}`);

      // 其他津貼
      const allowanceText = record.allowance_remark ? ` (${record.allowance_remark})` : '';
      details.push(`其他津貼：$${Number(record.allowance || 0).toFixed(2)}${allowanceText}`);

      // 強積金
      details.push(`強積金(僱主)：$${Number(record.mpf_employer || 0).toFixed(2)}`);
      details.push(`強積金(僱員)：$${Number(record.mpf_employee || 0).toFixed(2)}`);

      // 實發工資
      const actualSalary = Number(record.total_salary || 0) + Number(record.commission || 0) + Number(record.allowance || 0) + Number(record.ot_amount || 0) - Number(record.mpf_employee || 0);
      details.push(`實發工資：$${actualSalary.toFixed(2)}`);

      // 發票資訊（僅在 salary_type = 1 或 3 時顯示）
      if ((salaryType === 1 || salaryType === 3) && record.invoice_id) {
        const invoice = invoiceOptions.value?.find(inv => inv.id === record.invoice_id);
        if (invoice) {
          details.push(`發票：[${invoice.invoice_number}] ${invoice.name_cn} ${invoice.address}`);
        }
      }

      // 備註
      if (record.remark) {
        details.push(`備註：${record.remark}`);
      }

      // 如果有多個記錄，添加分隔線
      if (item.salary_records.length > 1 && index < item.salary_records.length - 1) {
        details.push(''); // 空行分隔不同的記錄
      }
    });

    return details.join('\n');
  };

  // 輔助函數
  const getSalaryTypeName = (salaryType) => {
    const types = { 1: '日薪', 2: '月薪', 3: '以工程項目計算' };
    return types[salaryType] || '';
  };

  const getSalaryReceiveMethodName = (methodId) => {
    const method = salaryReceiveMethodOptions.value?.find(m => m.id === methodId);
    return method?.name || '';
  };

  const formatYearMonth = (year, month) => {
    if (!year && !month) return '';
    const yearStr = year ? `${year}年` : '';
    const monthStr = month ? `${month}月` : '';
    return `${yearStr} ${monthStr}`.trim();
  };

  const formatCurrency = (amount) => {
    return `$${Number(amount || 0).toFixed(2)}`;
  };

  const calculateActualSalary = (item) => {
    if (!item.salary_records || item.salary_records.length === 0) {
      return 0;
    }
    return item.salary_records.reduce((sum, record) => {
      return sum + (Number(record.total_salary || 0) + Number(record.commission || 0) + Number(record.allowance || 0) + Number(record.ot_amount || 0) - Number(record.mpf_employee || 0));
    }, 0);
  };

  return {
    fetchExpenditures,
    tableColumns,
    perPage,
    totalRow,
    currentPage,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    dataMeta,
    expendituresList,
    refExpenditureListTable,
    ability,
    refetchData,
    filter_invoice_id,
    filter_user_id,
    filter_salary_type,
    filter_expense_status_id,
    filter_start_date,
    filter_end_date,
    filter_min_amount,
    filter_max_amount,

    invoiceOptions,
    workerOptions,
    expenseStatusOptions,
    userOptions,
    productOptions,
    salaryReceiveMethodOptions,
    exportRecords,
  };
}
